import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { Stack } from "@samdesk/components/Stack";
import { Text } from "@samdesk/components/Text";
import useCreateDashboardTab from "api/mutations/useCreateDashboardTab";
import { CreateDashboardTabInput, DashboardTabType } from "gql/graphql";
import AddTabIcon from "icons/addTab.svg";
import { useState } from "react";
import useNavigateWithAccount from "utils/useNavigateWithAccount";
import useSnackbar from "utils/useSnackbar";
import { menuItemSx } from "./Menu";

interface Props {
	handleCloseMenu: () => void;
}

export function TabOptionsMenu({ handleCloseMenu }: Props) {
	const [menuOptionsAnchorEl, setMenuOptionsAnchorEl] =
		useState<HTMLElement | null>(null);

	const { handleError } = useSnackbar();
	const navigateWithAccount = useNavigateWithAccount();

	const isOpen = Boolean(menuOptionsAnchorEl);

	const { mutateAsync: createDashboardTab } = useCreateDashboardTab();

	const handleNewAlertsTab = () => {
		const newAlertsTab = {
			alerts_tab_data: {
				bottom: [],
				name: "New Alerts Tab",
				top: [],
			},
			type: DashboardTabType.ALERTS,
		};

		createDashboardTab({ params: { tab: newAlertsTab } })
			.then((variables) => {
				if (variables.success && variables.tab) {
					navigateWithAccount(`alerts/${variables.tab.key}`);
				}
			})
			.catch((error) => handleError(error))
			.finally(() => handleCloseMenu());
	};

	const handleNewColumnsTab = () => {
		const newColumnsTab: CreateDashboardTabInput = {
			columns_tab_data: {
				name: "New Columns Tab",
			},
			type: DashboardTabType.COLUMNS,
		};

		createDashboardTab({ params: { tab: newColumnsTab } })
			.then((variables) => {
				if (variables.success && variables.tab) {
					navigateWithAccount(`columns/${variables.tab.key}`);
				}
			})
			.catch((error) => handleError(error))
			.finally(() => handleCloseMenu());
	};

	return (
		<>
			<MenuItem
				sx={menuItemSx({ hasHotkeys: false })}
				onClick={(e) => setMenuOptionsAnchorEl(e.currentTarget)}
			>
				<Box sx={itemLabelSx}>
					<AddTabIcon />
					<Typography noWrap sx={menuItemTextSx}>
						New Tab
					</Typography>
				</Box>
			</MenuItem>

			<Menu
				anchorEl={menuOptionsAnchorEl}
				anchorOrigin={{ horizontal: -11, vertical: -1 }}
				onClose={() => setMenuOptionsAnchorEl(null)}
				open={isOpen}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<MenuItem
					onClick={handleNewAlertsTab}
					sx={{
						...menuItemSx({ hasHotkeys: false }),
						width: "260px",
						height: "80px",
					}}
				>
					<Stack spacing="smaller">
						<Text weight="bold">Map View</Text>
						<Text variant="caption">
							View alerts in a single column alongside a map with various layers
							and visual details
						</Text>
					</Stack>
				</MenuItem>

				<MenuItem
					onClick={handleNewColumnsTab}
					sx={{
						...menuItemSx({ hasHotkeys: false }),
						width: "260px",
						height: "80px",
					}}
				>
					<Stack spacing="smaller">
						<Text weight="bold">Column View</Text>
						<Text variant="caption">
							Maximize the number of alerts on your screen by adding multiple
							columns of alerts
						</Text>
					</Stack>
				</MenuItem>
			</Menu>
		</>
	);
}

const menuItemTextSx = {
	marginLeft: "5px",
};

const itemLabelSx = {
	display: "flex",
};
